import type { AdminSummary } from "@chaochud/contracts";

/**
 * Admin session storage, deliberately keyed separately from `apps/web`
 * (`chaochud_admin_*` vs `chaochud_*`) so the two apps never share a
 * browser-storage session even when served on the same host during local
 * development (AGENTS.md §4 — admin must have a separate session scope).
 *
 * Same Phase 1 limitation as `apps/web`: tokens live in memory + localStorage
 * rather than httpOnly cookies; see `apps/web/src/lib/auth/token-storage.ts`.
 */
const ACCESS_TOKEN_KEY = "chaochud_admin_access_token";
const REFRESH_TOKEN_KEY = "chaochud_admin_refresh_token";
const ADMIN_KEY = "chaochud_admin_user";

interface MemoryState {
  accessToken: string | null;
  refreshToken: string | null;
  admin: AdminSummary | null;
}

const memory: MemoryState = {
  accessToken: null,
  refreshToken: null,
  admin: null,
};

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function hydrateAdminFromStorage(): MemoryState {
  if (!isBrowser()) {
    return memory;
  }

  memory.accessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);
  memory.refreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY);

  const rawAdmin = window.localStorage.getItem(ADMIN_KEY);
  memory.admin = rawAdmin ? (JSON.parse(rawAdmin) as AdminSummary) : null;

  return memory;
}

export function saveAdminSession(
  accessToken: string,
  refreshToken: string,
  admin: AdminSummary,
): void {
  memory.accessToken = accessToken;
  memory.refreshToken = refreshToken;
  memory.admin = admin;

  if (isBrowser()) {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    window.localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
  }
}

export function clearAdminSession(): void {
  memory.accessToken = null;
  memory.refreshToken = null;
  memory.admin = null;

  if (isBrowser()) {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    window.localStorage.removeItem(ADMIN_KEY);
  }
}

export function getAdminAccessToken(): string | null {
  return memory.accessToken;
}

export function getAdminRefreshToken(): string | null {
  return memory.refreshToken;
}

export function getStoredAdmin(): AdminSummary | null {
  return memory.admin;
}
